/**
 * persistence/managers/CheckpointManager.js
 *
 * Creates version checkpoints (full-string snapshots) and manages the
 * versions table in IndexedDB.
 *
 * A checkpoint is triggered by whichever fires first:
 *   1. Time trigger  — CHECKPOINT_INTERVAL_MS (5 min) elapsed since last checkpoint
 *   2. Event trigger — CHECKPOINT_EVENT_DELTA (10) new events added since last checkpoint
 *
 * Additional triggers (called explicitly, not auto-evaluated):
 *   3. api-success   — Called by SyncManager when the REST API push succeeds
 *   4. manual        — Future: user-initiated explicit save
 *   5. exit          — Called by the beforeunload handler
 *
 * Checkpoint state (lastCheckpointAt, lastCheckpointEventCount) is held
 * in memory.  It is re-initialised from IndexedDB on session start so
 * page refreshes do not reset the checkpoint clock.
 *
 * All methods are safe to call from the Redux middleware (sync context)
 * because IndexedDB writes are async and fire-and-forget.
 */

import { versionsTable } from "../db/database";
import { sessionsTable } from "../db/database";
import {
  CHECKPOINT_INTERVAL_MS,
  CHECKPOINT_EVENT_DELTA,
  VERSIONS_MAX_PER_SESSION,
} from "../config";
import { countEvents, parseEventTokens } from "./DiffManager";

// ─── In-memory state ──────────────────────────────────────────────────────────

/**
 * Per-session checkpoint state.
 * Key: `${matchId}__${analystId}` → { lastCheckpointAt, lastCheckpointEventCount }
 *
 * Populated by initCheckpointState() on InputBoard mount.
 */
const sessionState = new Map();

/**
 * Per-session Firestore checkpoint state — tracks when the last cloud snapshot
 * was pushed, independently of the IndexedDB checkpoint cadence.
 * Key: `${matchId}__${analystId}` → { lastFirestoreCheckpointAt }
 *
 * Kept separate so slowing the Firestore rate (90-min interval) has zero
 * effect on local IndexedDB checkpoints (still every 5 min).
 */
const firestoreCheckpointState = new Map();

// Note: version numbering now lives on the commit itself (BranchManager.commit
// derives the next number from the branch's commits), so no in-memory sequence
// counter is needed here any more.

// ─── Initialisation ───────────────────────────────────────────────────────────

/**
 * Seeds the in-memory checkpoint clocks from the commit history so they survive
 * page refreshes.  Commits are now the canonical version record, so:
 *   - the local checkpoint clock (5-min / 10-event cadence) seeds from the last
 *     commit, and
 *   - the Firestore clock (20/90-min gate) seeds from the last *pushed* commit
 *     — the true last cloud write, so reopening never slides the cloud window.
 *
 * Ensuring the branch exists also triggers the one-time migration that seeds
 * commits from any pre-existing legacy `versions` (see BranchManager).
 *
 * Call once per session, before evaluate() is first invoked.
 *
 * @param {string} matchId
 * @param {string} analystId
 */
export async function initCheckpointState(matchId, analystId) {
  const key = `${matchId}__${analystId}`;

  try {
    const { getOrCreateDefaultBranch, getCommits } = await import("../vcs/BranchManager");
    // Ensure the branch exists + migrate legacy versions → commits on first run.
    await getOrCreateDefaultBranch(matchId, analystId);

    const commits = await getCommits(matchId, analystId); // newest-first
    const lastCommit = commits[0] ?? null;
    const lastPushed = commits.find((c) => c.pushed) ?? null;

    sessionState.set(key, {
      lastCheckpointAt: lastCommit?.createdAt ?? 0,
      lastCheckpointEventCount: lastCommit?.eventCount ?? 0,
    });
    firestoreCheckpointState.set(key, {
      lastFirestoreCheckpointAt: lastPushed?.createdAt ?? 0,
    });
  } catch (error) {
    console.warn("[CheckpointManager] initCheckpointState failed:", error.message);
    sessionState.set(key, {
      lastCheckpointAt: 0,
      lastCheckpointEventCount: 0,
    });
    firestoreCheckpointState.set(key, { lastFirestoreCheckpointAt: 0 });
  }
}

// ─── Evaluation ───────────────────────────────────────────────────────────────

/**
 * Evaluates whether a checkpoint should be created based on the automatic
 * triggers (time + event-count).  Called on every SET_FULL_ANALYSE_STRING
 * action from the Redux middleware.
 *
 * Synchronous — does not block the Redux action.  The IndexedDB write is
 * fire-and-forget.
 *
 * @param {string} matchId
 * @param {string} analystId
 * @param {string} currentString  Latest event string from Redux
 */
export function evaluate(matchId, analystId, currentString) {
  const key = `${matchId}__${analystId}`;
  const state = sessionState.get(key);

  if (!state) {
    // State not initialised yet — skip silently.
    // initCheckpointState() will be called on mount; the next action will work.
    return;
  }

  const now = Date.now();
  const currentEventCount = countEvents(currentString);
  const timeDelta  = now - state.lastCheckpointAt;
  const eventDelta = currentEventCount - state.lastCheckpointEventCount;

  const timeTrigger  = timeDelta  >= CHECKPOINT_INTERVAL_MS;
  const eventTrigger = eventDelta >= CHECKPOINT_EVENT_DELTA;

  if (timeTrigger || eventTrigger) {
    const triggerType = timeTrigger && eventTrigger
      ? "time+event-count"
      : timeTrigger ? "time" : "event-count";

    // Update in-memory state immediately to prevent double-triggering
    sessionState.set(key, {
      lastCheckpointAt: now,
      lastCheckpointEventCount: currentEventCount,
    });

    // Fire-and-forget the IndexedDB write
    _createCheckpoint({ matchId, analystId, currentString, triggerType, currentEventCount, now });
  }
}

/**
 * Explicitly creates a checkpoint for a specific trigger type.
 * Called externally by SyncManager (api-success) and the beforeunload handler (exit).
 *
 * @param {object} params
 * @param {string} params.matchId
 * @param {string} params.analystId
 * @param {string} params.currentString
 * @param {string} params.triggerType   'api-success' | 'manual' | 'exit'
 * @returns {Promise<number|null>}  The new version id, or null on failure
 */
export async function createCheckpoint({ matchId, analystId, currentString, triggerType, skipFirestore = false }) {
  const key = `${matchId}__${analystId}`;
  const now = Date.now();
  const currentEventCount = countEvents(currentString);

  // Update in-memory state
  if (sessionState.has(key)) {
    sessionState.set(key, {
      lastCheckpointAt: now,
      lastCheckpointEventCount: currentEventCount,
    });
  }

  return _createCheckpoint({ matchId, analystId, currentString, triggerType, currentEventCount, now, skipFirestore });
}

/**
 * Creates a 'run-analysis' checkpoint — called from MatchUpdate when the analyst
 * taps "Run Analysis".  Always writes to both IDB and Firestore immediately
 * (no interval gate, no periodic-timer delay).
 *
 * Safe to call from any screen regardless of whether initCheckpointState() has
 * been invoked — the sessionless path in _createCheckpoint derives the correct
 * versionNumber from the IDB records directly.
 *
 * @param {object} params
 * @param {string} params.matchId
 * @param {string} params.analystId
 * @param {string} params.currentString  The match string at the moment of submission
 * @returns {Promise<number|null>}  The new IDB version id, or null on failure
 */
export async function createRunAnalysisCheckpoint({ matchId, analystId, currentString }) {
  return createCheckpoint({
    matchId,
    analystId,
    currentString,
    triggerType: "run-analysis",
  });
}

/**
 * Creates an 'exit' checkpoint when the analyst leaves (tab hidden / browser
 * close) — but ONLY if their work changed since the last cloud snapshot.
 *
 * The 'exit' trigger uses minInterval 0 in _createCheckpoint, so this always
 * writes to Firestore immediately, guaranteeing short-session analysts get a
 * cloud snapshot of their final state without waiting for the 20/90-min gates.
 *
 * The "changed since last cloud snapshot" guard compares the current event
 * tokens against the newest synced version, so rapid open/close cycles with no
 * edits do not generate duplicate Firestore writes.
 *
 * @param {object} params
 * @param {string} params.matchId
 * @param {string} params.analystId
 * @param {string} params.currentString  The live event string at exit time
 * @returns {Promise<number|null>}  The new IDB version id, or null if skipped
 */
export async function createExitCheckpoint({ matchId, analystId, currentString }) {
  try {
    const currentTokens = parseEventTokens(currentString ?? "");
    if (currentTokens.length === 0) return null; // nothing meaningful to snapshot

    // A device that only loaded the string (e.g. an idle second device) may hold
    // a stale copy; committing it on exit would overwrite newer cloud work.
    const { hasLocalEdits } = await import("../guard/stringGuard");
    if (!hasLocalEdits(matchId, analystId)) return null;

    // Compare against the last commit already pushed to the cloud — if nothing
    // changed since then, there's nothing new to snapshot on exit.
    const { getCommits } = await import("../vcs/BranchManager");
    const { loadDoc, docToString } = await import("../vcs/yjsDoc");
    const commits = await getCommits(matchId, analystId);
    const lastPushed = commits.find((c) => c.pushed) ?? null;
    if (lastPushed) {
      const pushedTokens = parseEventTokens(docToString(loadDoc(lastPushed.stateUpdate)));
      if (currentTokens.join(",") === pushedTokens.join(",")) return null;
    }

    return createCheckpoint({ matchId, analystId, currentString, triggerType: "exit" });
  } catch (error) {
    console.warn("[CheckpointManager] createExitCheckpoint failed:", error.message);
    return null;
  }
}

// ─── Read API ─────────────────────────────────────────────────────────────────

/**
 * Returns all version checkpoints for a session, newest-first.
 * Used by the history screen and useVersionHistory hook.
 *
 * @param {string} matchId
 * @param {string} analystId
 * @returns {Promise<object[]>}
 */
export async function getVersions(matchId, analystId) {
  try {
    // .sortBy() in Dexie always sorts ascending — returns oldest-first.
    // The caller (useVersionHistory) reverses this to get newest-first.
    // Note: .reverse() has no effect when combined with .sortBy(), so it
    // is intentionally omitted here.
    return await versionsTable()
      .where("[matchId+analystId]")
      .equals([matchId, analystId])
      .sortBy("createdAt");
  } catch (error) {
    console.warn("[CheckpointManager] getVersions failed:", error.message);
    return [];
  }
}

/**
 * Returns the most recent version checkpoint for a session.
 *
 * @param {string} matchId
 * @param {string} analystId
 * @returns {Promise<object|null>}
 */
export async function getLatestVersion(matchId, analystId) {
  try {
    return await versionsTable()
      .where("[matchId+analystId]")
      .equals([matchId, analystId])
      .last() || null;
  } catch (error) {
    console.warn("[CheckpointManager] getLatestVersion failed:", error.message);
    return null;
  }
}


/**
 * Prunes old version checkpoints that exceed VERSIONS_MAX_PER_SESSION.
 * Deletes the oldest first.  Called on session start.
 *
 * @param {string} matchId
 * @param {string} analystId
 */
export async function pruneVersions(matchId, analystId) {
  try {
    const all = await versionsTable()
      .where("[matchId+analystId]")
      .equals([matchId, analystId])
      .sortBy("createdAt");

    const excess = all.length - VERSIONS_MAX_PER_SESSION;
    if (excess > 0) {
      const idsToDelete = all.slice(0, excess).map((r) => r.id);
      await versionsTable().bulkDelete(idsToDelete);
    }
  } catch (error) {
    console.warn("[CheckpointManager] pruneVersions failed:", error.message);
  }
}

// ─── Private ──────────────────────────────────────────────────────────────────

/**
 * Human-friendly commit messages per trigger type.  Used as the commit message
 * so the History page / manager portal read naturally.
 */
const TRIGGER_MESSAGES = {
  "time":             "Auto save · 5 min",
  "event-count":      "Auto save · 10 events",
  "time+event-count": "Auto save",
  "api-success":      "Saved to backup history",
  "manual":           "Manual save",
  "exit":             "Saved on exit",
  "run-analysis":     "Run analysis",
  "techqc-edit":      "TechQC edit",
  "techqc-delete":    "TechQC delete",
  "techqc-submit":    "TechQC submit",
};

function _triggerMessage(triggerType) {
  return TRIGGER_MESSAGES[triggerType] ?? "Checkpoint";
}

/**
 * Creates the canonical version record for a checkpoint — now a Yjs COMMIT
 * (not a legacy `versions` row).  Every checkpoint trigger funnels here.
 *
 * The commit is created from `currentString` (BranchManager reconciles it into
 * the analyst's working branch), then cloud-pushed on the same per-trigger gate
 * the legacy system used, so auto-commits stay within the Firestore budget while
 * manual / exit / run-analysis commits reach the cloud immediately.
 *
 * @returns {Promise<string|null>}  The new commitId or null on failure
 */
async function _createCheckpoint({ matchId, analystId, currentString, triggerType, now, skipFirestore = false }) {
  try {
    const key = `${matchId}__${analystId}`;

    // 1. Create the commit (canonical history) from the current string.
    const { commitFromString } = await import("../vcs/BranchManager");
    const commitRow = await commitFromString({
      matchId,
      analystId,
      currentString,
      message: _triggerMessage(triggerType),
      triggerType,
      createdAt: now,
    });

    // 2. Keep session.lastCheckpointAt fresh (used by recovery heuristics).
    const session = await sessionsTable()
      .where("[matchId+analystId]")
      .equals([matchId, analystId])
      .first();
    if (session) {
      await sessionsTable().update(session.id, { lastCheckpointAt: now });
    }

    // 3. Gated cloud push — reuse the legacy per-trigger interval logic.
    //    manual / exit / run-analysis → 0 ms (push immediately)
    //    api-success                  → 20 min
    //    time / events                → 90 min
    try {
      const {
        FIRESTORE_CHECKPOINT_MIN_INTERVAL_MS,
        FIRESTORE_API_SUCCESS_MIN_INTERVAL_MS,
      } = await import("../config");

      const minInterval =
        triggerType === "manual" || triggerType === "exit" || triggerType === "run-analysis"
          ? 0
          : triggerType === "api-success"
          ? FIRESTORE_API_SUCCESS_MIN_INTERVAL_MS
          : FIRESTORE_CHECKPOINT_MIN_INTERVAL_MS;

      const fsState = firestoreCheckpointState.get(key) ?? { lastFirestoreCheckpointAt: 0 };
      const shouldWriteToFirestore =
        !skipFirestore &&
        commitRow &&
        (now - fsState.lastFirestoreCheckpointAt) >= minInterval;

      if (shouldWriteToFirestore) {
        firestoreCheckpointState.set(key, { lastFirestoreCheckpointAt: now });
        const { pushCommit } = await import("../vcs/vcsFirestore");
        const pushPromise = pushCommit({ matchId, analystId, commit: commitRow });
        // Immediate triggers (minInterval 0) await so the commit lands before
        // navigation / unload; gated auto triggers are fire-and-forget.
        if (minInterval === 0) {
          await pushPromise;
        } else {
          pushPromise.catch((e) =>
            console.warn("[CheckpointManager] pushCommit failed:", e.message)
          );
        }
      }
    } catch (fsErr) {
      // Cloud push failure is non-fatal — the commit is safe in IndexedDB.
      console.warn("[CheckpointManager] cloud push failed:", fsErr.message);
    }

    return commitRow?.commitId ?? null;
  } catch (error) {
    console.warn("[CheckpointManager] _createCheckpoint failed:", error.message);
    return null;
  }
}
